import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Moon, Plus, Search, Copy, Archive, Eye } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft:    "bg-gray-100 text-gray-700",
  active:   "bg-green-100 text-green-800",
  inactive: "bg-yellow-100 text-yellow-800",
  archived: "bg-red-100 text-red-700",
};

const STATUS_FILTERS = ["All", "draft", "active", "inactive", "archived"];

const CATEGORY_LABELS = {
  comprehensive:   "Comprehensive",
  without_visa:    "Without Visa",
  custom:          "Custom",
  land_only:       "Land Only",
  other:           "Other",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

function lowestPrice(pkg) {
  const occ = [pkg.occupancy_double, pkg.occupancy_triple, pkg.occupancy_quad, pkg.occupancy_single];
  const prices = occ.filter(o => o?.enabled && o?.selling_price).map(o => o.selling_price);
  if (!prices.length) return null;
  return Math.min(...prices);
}

export default function UmrahPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/umrah-packages`, { headers: getAuthHeaders() });
      setPackages(r.data || []);
    } catch { /**/ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = packages.filter(p => {
    if (statusFilter !== "All" && p.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (p.package_name || "").toLowerCase().includes(q) ||
        (p.package_reference || "").toLowerCase().includes(q) ||
        (p.makkah?.hotel_name || "").toLowerCase().includes(q) ||
        (p.madinah?.hotel_name || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  const handleDuplicate = async (pkg, e) => {
    e.stopPropagation();
    setActionLoading(`dup-${pkg.id}`);
    try {
      const r = await axios.post(`${API}/umrah-packages/${pkg.id}/duplicate`, {}, { headers: getAuthHeaders() });
      await load();
      navigate(`/umrah-packages/${r.data.id}`);
    } catch { /**/ } finally {
      setActionLoading(null);
    }
  };

  const handleArchive = async (pkg, e) => {
    e.stopPropagation();
    if (!window.confirm(`Archive "${pkg.package_name}"? It will be preserved and can be restored.`)) return;
    setActionLoading(`arch-${pkg.id}`);
    try {
      await axios.patch(`${API}/umrah-packages/${pkg.id}/status`, { status: "archived" }, { headers: getAuthHeaders() });
      await load();
    } catch { /**/ } finally {
      setActionLoading(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Moon className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Umrah Packages</h1>
              <p className="text-sm text-gray-500">{packages.length} package{packages.length !== 1 ? "s" : ""}</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/umrah-packages/new")}
            className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-4 py-2 text-sm font-semibold"
          >
            <Plus size={16} />
            New Package
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {STATUS_FILTERS.map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`px-3 py-1 rounded-md text-xs font-semibold capitalize transition-colors ${
                  statusFilter === f ? "bg-white shadow text-amber-800" : "text-gray-500 hover:text-gray-800"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search packages, hotels…"
              className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              {packages.length === 0 ? "No packages yet. Create your first Umrah package." : "No packages match your search."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Package Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Travel Period</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Nights</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Makkah</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Madinah</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">From</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(pkg => {
                    const minPrice = lowestPrice(pkg);
                    return (
                      <tr
                        key={pkg.id}
                        onClick={() => navigate(`/umrah-packages/${pkg.id}`)}
                        className="hover:bg-amber-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 font-mono text-xs text-amber-800 font-semibold whitespace-nowrap">
                          {pkg.package_reference || "—"}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 max-w-xs">
                          {pkg.package_name || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {pkg.flexible_dates
                            ? <span className="text-xs italic text-gray-400">Flexible</span>
                            : pkg.travel_start_date
                              ? `${fmtDate(pkg.travel_start_date)} – ${fmtDate(pkg.travel_end_date)}`
                              : "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-700 text-center">
                          {pkg.total_nights || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">
                          {CATEGORY_LABELS[pkg.category] || pkg.category || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs max-w-[120px] truncate">
                          {pkg.makkah?.hotel_name
                            ? `${pkg.makkah.hotel_name}${pkg.makkah.nights ? ` (${pkg.makkah.nights}n)` : ""}`
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs max-w-[120px] truncate">
                          {pkg.madinah?.hotel_name
                            ? `${pkg.madinah.hotel_name}${pkg.madinah.nights ? ` (${pkg.madinah.nights}n)` : ""}`
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3 font-semibold text-amber-800 whitespace-nowrap">
                          {minPrice ? `€${minPrice.toFixed(0)}/pp` : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${STATUS_COLORS[pkg.status] || "bg-gray-100 text-gray-600"}`}>
                            {pkg.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                            <button
                              title="View / Edit"
                              onClick={() => navigate(`/umrah-packages/${pkg.id}`)}
                              className="p-1.5 text-gray-400 hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors"
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              title="Duplicate"
                              disabled={actionLoading === `dup-${pkg.id}`}
                              onClick={e => handleDuplicate(pkg, e)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors disabled:opacity-40"
                            >
                              <Copy size={14} />
                            </button>
                            {pkg.status !== "archived" && (
                              <button
                                title="Archive"
                                disabled={actionLoading === `arch-${pkg.id}`}
                                onClick={e => handleArchive(pkg, e)}
                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-40"
                              >
                                <Archive size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
